import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import toast, { Toaster } from 'react-hot-toast';
import './NewsFeed.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function NewsFeed() {
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('articles');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [topicFilter, setTopicFilter] = useState('all');
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    // Load topics
    fetch(`${API_URL}/api/topics`)
      .then(res => res.json())
      .then(data => setTopics(data.topics || []))
      .catch(err => console.error('Topics error:', err));
    
    loadData();
    
    const eventSource = new EventSource(`${API_URL}/api/stream`);
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_article') {
          setArticles(prev => [data.data, ...prev]);
          setLastUpdate(new Date());
          
          // Show toast notification
          toast.success('🔥 New article published!', {
            duration: 4000,
            position: 'top-right',
          });
        } else if (data.type === 'new_event') {
          setEvents(prev => [data.data, ...prev]);
          setLastUpdate(new Date());
          
          toast('📰 New event detected', {
            duration: 3000,
            position: 'top-right',
            icon: '🔔',
          });
        }
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    };
    
    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
      eventSource.close();
    };
    
    return () => {
      eventSource.close();
    };
  }, []);

  const loadData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Build URL with topic filter
      let articlesUrl = `${API_URL}/api/articles?limit=30`;
      if (topicFilter && topicFilter !== 'all') {
        articlesUrl += `&topic=${topicFilter}`;
      }
      
      const [articlesRes, eventsRes] = await Promise.all([
        fetch(articlesUrl),
        fetch(`${API_URL}/api/events?limit=30`)
      ]);
      
      const articlesData = await articlesRes.json();
      const eventsData = await eventsRes.json();
      
      setArticles(articlesData);
      setEvents(eventsData);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to load news. Please try again.');
      console.error('Load error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadData(true);
    toast.success('Refreshing feed...', {
      duration: 2000,
      position: 'top-right',
    });
  };

  const filteredArticles = articles.filter(article => {
    if (filter === 'all') return true;
    const level = article.confidence_score >= 0.85 ? 'high' : 
                  article.confidence_score >= 0.60 ? 'medium' : 'low';
    return level === filter;
  });

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    const level = event.confidence_score >= 0.85 ? 'high' : 
                  event.confidence_score >= 0.60 ? 'medium' : 'low';
    return level === filter;
  });
  
  // Reload when topic filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loading) {
      loadData();
    }
  }, [topicFilter]);

  if (loading && articles.length === 0) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <span style={{ marginLeft: '12px' }}>Loading news...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <Toaster />
      <div className="news-feed">
        <div className="feed-header">
          <div className="feed-title-section">
            <h1 className="feed-title">Real-Time News Feed</h1>
            <div className="live-indicator">
              <span className="live-dot"></span>
              <span>Live Updates</span>
            </div>
            <button 
              className="refresh-button btn btn-outline"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-button"
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
          
          <div className="feed-controls">
            <div className="view-toggle">
              <button
                className={`toggle-btn ${view === 'articles' ? 'active' : ''}`}
                onClick={() => setView('articles')}
              >
                Articles
              </button>
              <button
                className={`toggle-btn ${view === 'events' ? 'active' : ''}`}
                onClick={() => setView('events')}
              >
                Events
              </button>
            </div>
            
            {topics.length > 0 && view === 'articles' && (
              <div className="topic-filter">
                <select 
                  value={topicFilter} 
                  onChange={(e) => setTopicFilter(e.target.value)}
                  className="topic-select"
                >
                  {topics.map(topic => (
                    <option key={topic.id} value={topic.id}>
                      {topic.icon} {topic.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="filter-buttons">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-btn ${filter === 'high' ? 'active' : ''}`}
                onClick={() => setFilter('high')}
              >
                High Confidence
              </button>
              <button
                className={`filter-btn ${filter === 'medium' ? 'active' : ''}`}
                onClick={() => setFilter('medium')}
              >
                Medium
              </button>
              <button
                className={`filter-btn ${filter === 'low' ? 'active' : ''}`}
                onClick={() => setFilter('low')}
              >
                Low
              </button>
            </div>
          </div>
          
          <div className="last-update">
            Last updated: {format(lastUpdate, 'HH:mm:ss')}
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {view === 'articles' && (
          <div className="articles-grid">
            {filteredArticles.length === 0 ? (
              <div className="empty-state">
                <p>No articles match your filters. Monitoring continues automatically...</p>
                <button className="btn btn-primary" onClick={handleRefresh}>
                  Refresh Now
                </button>
              </div>
            ) : (
              filteredArticles.map(article => (
                <ArticleCard key={article.id} article={article} />
              ))
            )}
          </div>
        )}

        {view === 'events' && (
          <div className="events-grid">
            {filteredEvents.length === 0 ? (
              <div className="empty-state">
                <p>No events match your filters.</p>
              </div>
            ) : (
              filteredEvents.map(event => (
                <EventCard key={event.id} event={event} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ArticleCard({ article }) {
  const confidenceLevel = article.confidence_score >= 0.85 ? 'high' : 
                          article.confidence_score >= 0.60 ? 'medium' : 'low';
  const confidenceText = article.confidence_score >= 0.85 ? 'Verified' : 
                         article.confidence_score >= 0.60 ? 'Emerging' : 'Unverified';

  return (
    <Link to={`/article/${article.id}`} className="article-card card" data-testid="article-card">
      <div className="card-header">
        <div className="ai-badge">
          <span className="ai-badge-icon">✨</span>
          AI-Generated
        </div>
        <div className={`confidence-badge ${confidenceLevel}`}>
          {confidenceText} {Math.round(article.confidence_score * 100)}%
        </div>
      </div>
      
      <h3 className="article-card-title">{article.title}</h3>
      
      <p className="article-preview">
        {article.content?.substring(0, 150)}...
      </p>
      
      <div className="article-meta">
        <span className="meta-item">
          📅 {format(new Date(article.generated_at), 'MMM d, yyyy HH:mm')}
        </span>
        {article.sources && (
          <span className="meta-item">
            📊 {article.sources.length} sources
          </span>
        )}
      </div>
    </Link>
  );
}

function EventCard({ event }) {
  const confidenceLevel = event.confidence_score >= 0.85 ? 'high' : 
                          event.confidence_score >= 0.60 ? 'medium' : 'low';
  const confidenceText = event.confidence_score >= 0.85 ? 'Verified' : 
                         event.confidence_score >= 0.60 ? 'Emerging' : 'Developing';

  return (
    <Link to={`/event/${event.id}`} className="event-card card" data-testid="event-card">
      <div className="card-header">
        <div className={`confidence-badge ${confidenceLevel}`}>
          {confidenceText} {Math.round(event.confidence_score * 100)}%
        </div>
      </div>
      
      <h3 className="event-card-title">{event.title}</h3>
      
      {event.summary && (
        <p className="event-summary">{event.summary}</p>
      )}
      
      <div className="event-meta">
        <span className="meta-item">
          📊 {event.source_count} sources
        </span>
        <span className="meta-item">
          🌐 {event.platforms?.join(', ')}
        </span>
        <span className="meta-item">
          📅 {format(new Date(event.detected_at), 'MMM d, HH:mm')}
        </span>
      </div>
    </Link>
  );
}

export default NewsFeed;
